import { Modal, Table, TableColumnsType, Button, Space, Tag, Form, Input, Switch, message } from 'antd';
import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  ADDRESS_CONFIG_LIST,
  CREATE_ADDRESS_CONFIG,
  UPDATE_ADDRESS_CONFIG,
  DELETE_ADDRESS_CONFIG,
  AddressConfigItem,
  AddressConfigListResponse,
} from '../graphql/queries/getAddressConfigList';

const { TextArea } = Input;

interface AddressConfigModalProps {
  visible: boolean;
  onClose: () => void;
}

interface ConfigFormValues {
  configKey: string;
  configValue: string;
  description?: string;
  isActive: boolean;
}

const AddressConfigModal: React.FC<AddressConfigModalProps> = ({ visible, onClose }) => {
  const [formVisible, setFormVisible] = useState(false);
  const [editing, setEditing] = useState<AddressConfigItem | null>(null);
  const [form] = Form.useForm<ConfigFormValues>();

  const { data, loading, refetch } = useQuery<AddressConfigListResponse>(ADDRESS_CONFIG_LIST, {
    variables: { activeOnly: false },
    skip: !visible,
    fetchPolicy: 'cache-and-network',
  });

  const [createConfig, { loading: creating }] = useMutation(CREATE_ADDRESS_CONFIG);
  const [updateConfig, { loading: updating }] = useMutation(UPDATE_ADDRESS_CONFIG);
  const [deleteConfig] = useMutation(DELETE_ADDRESS_CONFIG, {
    onCompleted: () => {
      message.success('删除成功');
      void refetch();
    },
    onError: (err) => message.error(`删除失败：${err.message}`),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ isActive: true });
    setFormVisible(true);
  };

  const openEdit = (record: AddressConfigItem) => {
    setEditing(record);
    form.setFieldsValue({
      configKey: record.configKey,
      configValue: record.configValue,
      description: record.description ?? undefined,
      isActive: record.isActive,
    });
    setFormVisible(true);
  };

  const showDeleteConfirm = (record: AddressConfigItem) => {
    Modal.confirm({
      title: '确认删除',
      content: `确认删除配置「${record.configKey}」？`,
      okText: '确认',
      okType: 'danger',
      cancelText: '取消',
      onOk: () => deleteConfig({ variables: { id: Number(record.id) } }),
    });
  };

  const handleSubmit = async () => {
    let values: ConfigFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    try {
      if (editing) {
        await updateConfig({
          variables: {
            id: Number(editing.id),
            input: {
              configValue: values.configValue,
              description: values.description ?? null,
              isActive: values.isActive,
            },
          },
        });
        message.success('更新成功');
      } else {
        await createConfig({
          variables: {
            input: {
              configKey: values.configKey.trim(),
              configValue: values.configValue,
              description: values.description ?? null,
              isActive: values.isActive,
            },
          },
        });
        message.success('创建成功');
      }
      setFormVisible(false);
      void refetch();
    } catch (err: any) {
      message.error(`保存失败：${err?.message ?? '未知错误'}`);
    }
  };

  const columns: TableColumnsType<AddressConfigItem> = [
    {
      title: '配置键',
      dataIndex: 'configKey',
      key: 'configKey',
      width: 200,
      ellipsis: true,
    },
    {
      title: '配置值',
      dataIndex: 'configValue',
      key: 'configValue',
      width: 320,
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
      render: (text: string | null) => text || '-',
    },
    {
      title: '启用',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      align: 'center',
      render: (active: boolean) =>
        active ? <Tag color="green">启用</Tag> : <Tag color="red">禁用</Tag>,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 180,
      align: 'center',
      render: (text: string | null) => (text ? new Date(text).toLocaleString() : '-'),
    },
    {
      title: '操作',
      key: 'action',
      width: 130,
      fixed: 'right' as const,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Button type="link" size="small" danger onClick={() => showDeleteConfirm(record)}>
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="灰度配置"
        open={visible}
        onCancel={onClose}
        width={1000}
        footer={<Button onClick={onClose}>关闭</Button>}
      >
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={openCreate}>
            新增配置
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={data?.addressConfigList || []}
          loading={loading}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          pagination={false}
        />
      </Modal>

      <Modal
        title={editing ? '编辑配置' : '新增配置'}
        open={formVisible}
        onCancel={() => setFormVisible(false)}
        onOk={handleSubmit}
        okText="保存"
        cancelText="取消"
        confirmLoading={creating || updating}
        destroyOnHidden
      >
        <Form<ConfigFormValues> form={form} layout="vertical">
          <Form.Item
            label="配置键"
            name="configKey"
            rules={[{ required: true, message: '请输入配置键' }]}
          >
            <Input placeholder="请输入配置键" disabled={!!editing} />
          </Form.Item>
          <Form.Item
            label="配置值（JSON）"
            name="configValue"
            rules={[{ required: true, message: '请输入配置值' }]}
          >
            <TextArea rows={6} placeholder="请输入配置值（JSON 格式）" />
          </Form.Item>
          <Form.Item label="描述" name="description">
            <Input placeholder="请输入配置描述" />
          </Form.Item>
          <Form.Item label="是否启用" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AddressConfigModal;
